import { ITimeoutService, IRootScopeService } from "angular";
import { IModalScope, IModalService, IModalServiceInstance } from "angular-ui-bootstrap";

import { IKpi, IKpiGroup, IKpiGroupSetting, IModifiableKpi } from "../models/models";
import { KpiService } from "../services/kpiService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { byTextField } from "@gtmhub/util/sorting-utils";
import { IdMap, toKeyMap } from "@gtmhub/util";
import { IKpiItems, ungroupedGroupId } from "@gtmhub/kpis";

export interface IKpiGroupingFormData {
  kpi: IKpi;
  groups: IKpiGroup[];
  groupSettings: IKpiGroupSetting[];
  kpisMap: IdMap<IKpiItems>;
  isReorderKpiEnabled: boolean;
  beginNewGroup(): unknown;
}

export interface IKpiGroupingFormOpener {
  open(
    kpi: IKpi,
    groupSettings: IKpiGroupSetting[],
    kpisMap: IdMap<IKpiItems>,
    groups: IKpiGroup[],
    beginNewGroup: () => unknown,
    isReorderKpiEnabled: boolean
  ): IModalServiceInstance;
}

export class KpiGroupingFormOpener implements IKpiGroupingFormOpener {
  public static $inject = ["$uibModal"];

  constructor(private $uibModal: IModalService) {}

  public open(
    kpi: IKpi,
    groupSettings: IKpiGroupSetting[],
    kpisMap: IdMap<IKpiItems>,
    groups: IKpiGroup[],
    beginNewGroup: () => unknown,
    isReorderKpiEnabled: boolean
  ): IModalServiceInstance {
    return this.$uibModal.open({
      templateUrl: require("../views/grouping-form.jade"),
      windowClass: "medium-large kpi-modal",
      backdropClass: "dark-backdrop",
      backdrop: false,
      resolve: {
        formData: (): IKpiGroupingFormData => ({
          kpi,
          groupSettings,
          kpisMap,
          groups,
          beginNewGroup,
          isReorderKpiEnabled,
        }),
      },
      controller: "KpiGroupingFormCtrl",
    });
  }
}

export interface IKpiGroupingFormScope extends IModalScope {
  indicators: {
    patching: boolean;
  };
  groups: IKpiGroup[];
  selectedGroups: string[];
  filter: {
    query: string;
  };
  save(): void;
  toggleGroup(groupId: string): void;
  isSelected(groupId: string): boolean;
  groupName(groupId: string): string;
  clearQuery(): void;
  beginNewGroup(): void;
}

export class KpiGroupingFormCtrl {
  public static $inject = ["$scope", "$rootScope", "$timeout", "KpiService", "UIErrorHandlingService", "formData"];

  private groupsMap: { [id: string]: IKpiGroup };

  constructor(
    private $scope: IKpiGroupingFormScope,
    private $rootScope: IRootScopeService,
    private $timeout: ITimeoutService,
    private kpiService: KpiService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private formData: IKpiGroupingFormData
  ) {
    const groups = [...formData.groups].sort(byTextField("name"));
    this.groupsMap = groups.reduce((result, group) => Object.assign(result, { [group.id]: group }), {});

    this.$scope.indicators = {
      patching: false,
    };
    this.$scope.filter = {
      query: "",
    };
    this.$scope.groups = groups;
    this.$scope.selectedGroups = [...formData.kpi.groups];
    this.$scope.save = this.save;
    this.$scope.toggleGroup = this.toggleGroup;
    this.$scope.isSelected = this.isSelected;
    this.$scope.groupName = this.groupName;
    this.$scope.clearQuery = this.clearQuery;
    this.$scope.beginNewGroup = this.beginNewGroup;
  }

  private save = (): void => {
    if (this.formData.isReorderKpiEnabled) {
      const kpiForGroupEdit: IModifiableKpi = {
        id: this.formData.kpi.id,
        groups: [...this.$scope.selectedGroups],
      };

      if (this.formData.kpi.groupsOrder) {
        kpiForGroupEdit.groupsOrder = this.formData.kpi.groupsOrder;
      }

      const kpisToPatch: IModifiableKpi[] = [kpiForGroupEdit];
      const removedGroups = this.formData.kpi.groups.filter((previousGroup) => !kpiForGroupEdit.groups.includes(previousGroup));
      const addedGroups = kpiForGroupEdit.groups.filter((patchableGroup) => !this.formData.kpi.groups.includes(patchableGroup));

      if (addedGroups.length > 0) {
        addedGroups.forEach((addedGroup) => {
          this.addGroupOrderForOrderedGroup(kpiForGroupEdit, addedGroup);
        });

        if (this.formData.kpi.groups.length === 0) {
          const ungroupedKpisToPatch = this.findOrderedKpisToReorder(ungroupedGroupId);
          kpisToPatch.push(...ungroupedKpisToPatch);

          if (kpiForGroupEdit.groupsOrder && kpiForGroupEdit.groupsOrder[ungroupedGroupId] !== undefined) {
            delete kpiForGroupEdit.groupsOrder[ungroupedGroupId];
          }
        }
      }

      if (removedGroups.length > 0) {
        removedGroups.forEach((removedGroup) => {
          const removedGroupKpisToPatch = this.findOrderedKpisToReorder(removedGroup);
          kpisToPatch.push(...removedGroupKpisToPatch);

          if (kpiForGroupEdit.groupsOrder && kpiForGroupEdit.groupsOrder[removedGroup] !== undefined) {
            delete kpiForGroupEdit.groupsOrder[removedGroup];
          }
        });

        if (kpiForGroupEdit.groups.length === 0) {
          this.addGroupOrderForOrderedGroup(kpiForGroupEdit, ungroupedGroupId);
        }
      }

      this.kpiService.patchKpis(toKeyMap("id", kpisToPatch)).then(
        () => {
          const kpiClone = angular.copy(this.formData.kpi);
          kpiClone.groups = kpisToPatch[0].groups;
          kpiClone.groupsOrder = kpisToPatch[0].groupsOrder;
          kpisToPatch.shift();
          this.$rootScope.$broadcast("kpiUpdated", kpiClone, kpisToPatch);
          this.$scope.$close();
        },
        (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "patching", inline: true })
      );
    } else {
      const patchableKpi: IModifiableKpi = {
        groups: [...this.$scope.selectedGroups],
      };
      this.kpiService.patchKpi(this.formData.kpi.id, patchableKpi).then(
        () => {
          const kpiClone = angular.copy(this.formData.kpi);
          kpiClone.groups = patchableKpi.groups;
          this.$rootScope.$broadcast("kpiUpdated", kpiClone);
          this.$scope.$close();
        },
        (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "patching", inline: true })
      );
    }
  };

  private addGroupOrderForOrderedGroup = (kpiForGroupEdit: IModifiableKpi, groupId: string) => {
    const kpiIds = this.formData.groupSettings.find((setting) => setting.groupId === groupId).kpiIds;
    if (kpiIds && this.isGroupOrdered(kpiIds[0], groupId)) {
      kpiForGroupEdit.groupsOrder = kpiForGroupEdit.groupsOrder || {};
      kpiForGroupEdit.groupsOrder[groupId] = kpiIds.length;
    }
  };

  private findOrderedKpisToReorder = (groupId: string) => {
    const kpisToPatch: IModifiableKpi[] = [];

    const kpiIds = this.formData.groupSettings.find((setting) => setting.groupId === groupId).kpiIds;
    kpiIds.splice(kpiIds.indexOf(this.formData.kpi.id), 1);

    if (this.isGroupOrdered(kpiIds[0], groupId)) {
      for (let i = 0; i < kpiIds.length; i++) {
        const ungroupedKpiToPatch: IModifiableKpi = {};
        ungroupedKpiToPatch.id = kpiIds[i];
        ungroupedKpiToPatch.groupsOrder = this.formData.kpisMap[kpiIds[i]].kpi.groupsOrder || {};
        ungroupedKpiToPatch.groupsOrder[groupId] = i;
        kpisToPatch.push(ungroupedKpiToPatch);
      }
    }

    return kpisToPatch;
  };

  private isGroupOrdered = (kpiInGroup: string, groupId: string): boolean => {
    return (
      this.formData.kpisMap[kpiInGroup] && this.formData.kpisMap[kpiInGroup].kpi.groupsOrder && this.formData.kpisMap[kpiInGroup].kpi.groupsOrder[groupId] !== undefined
    );
  };

  private toggleGroup = (groupId: string): void => {
    if (this.$scope.selectedGroups.indexOf(groupId) < 0) {
      this.$scope.selectedGroups.push(groupId);
    } else {
      this.$scope.selectedGroups = this.$scope.selectedGroups.filter((id) => id !== groupId);
    }
  };

  private isSelected = (groupId: string): boolean => {
    return this.$scope.selectedGroups.indexOf(groupId) >= 0;
  };

  private groupName = (groupId: string): string => {
    const group = this.groupsMap[groupId];
    return group ? group.name : null;
  };

  private clearQuery = (): void => {
    this.$scope.filter.query = "";
  };

  private beginNewGroup = (): void => {
    this.$scope.$close();
    this.$timeout(() => this.formData.beginNewGroup());
  };
}
